import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useMatch } from 'react-router-dom';
import { ROUTES, PERMISSIONS } from '@/constants';
import { useAuthContext } from '@/context/AuthContext';
import { filterNavItems } from '@/utils/navAccess';
import { cn } from '@/lib/utils';
import {
    APP_SIDEBAR_ASIDE_CLASS,
    APP_SIDEBAR_HEADER_ROW_CLASS,
    APP_SIDEBAR_TOGGLE_BUTTON_CLASS,
    appSidebarNavClassName,
    APP_SIDEBAR_SECTION_LABEL_CLASS,
    appSidebarNavLinkClassName,
} from '@/lib/sidebarNav';
import { ChevronsUp, LayoutDashboard, FolderKanban, Users, BookOpen, FileDown, Network, MonitorCog } from 'lucide-react';
import { pillarChairService } from '@/features/pillarChair/services/pillarChairService';
import { TripleChevronCloseIcon, TripleChevronOpenIcon } from '@/components/layout/PortalSidebarToggle';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ExecutiveAppHeader } from '@/components/layout/ExecutiveAppHeader';
import { DashboardHeaderSlotProvider, DashboardHeaderSlot } from '@/context/DashboardHeaderSlotContext';
import { ChatProvider } from '@/features/chat/context/ChatContext';
import { AiAssistantProvider } from '@/features/aiAssistant/context/AiAssistantContext';
import { AiAssistantNavItem } from '@/features/aiAssistant/components/AiAssistantNavItem';
import { useOperationalDashboardAccess } from '@/features/operationalDashboard/context/useOperationalDashboardAccess';

export function PillarChairLayout() {
    const { user } = useAuthContext();
    const [pillarNameSubtitle, setPillarNameSubtitle] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const isMobile = useIsMobile();
    const isPillarChairDashboardHome = Boolean(useMatch({ path: ROUTES.PILLAR_CHAIR, end: true }));

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const body = await pillarChairService.getMyPillar();
                const p = body?.data;
                const name = typeof p?.name === 'string' ? p.name.trim() : '';
                if (!cancelled) setPillarNameSubtitle(name || null);
            } catch {
                if (!cancelled) setPillarNameSubtitle(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [user?.user?.id]);

    const pillarOutletCtx = {
        projectService: pillarChairService,
        basePath: ROUTES.PILLAR_CHAIR,
        skipProjectPicker: false,
        // The project → task drill-down reachable from "Project Management" is
        // browse-only for Pillar Chair (per product decision, not a permission gate).
        viewOnly: true,
    };

    const { hasAccess: hasOperationalDashboard } = useOperationalDashboardAccess();

    const tabs = useMemo(
        () => [
            {
                label: 'Dashboard',
                path: ROUTES.PILLAR_CHAIR,
                end: true,
                icon: LayoutDashboard,
                requiredPermissions: [PERMISSIONS.VIEW_PILLAR],
            },
            {
                label: 'Project Management',
                path: ROUTES.PILLAR_CHAIR_PROJECTS,
                icon: FolderKanban,
                requiredPermissions: [PERMISSIONS.VIEW_PILLAR],
            },
            {
                label: 'Escalation History',
                path: ROUTES.PILLAR_CHAIR_ESCALATION_HISTORY,
                icon: ChevronsUp,
                requiredPermissions: [PERMISSIONS.VIEW_PILLAR],
            },
            {
                label: 'Structure',
                path: ROUTES.PILLAR_CHAIR_STRUCTURE,
                icon: Network,
                // No permission or role gate: every account may see the structure.
                // Where the tree is rooted per role is decided by the server.
            },
            {
                label: 'Export',
                path: ROUTES.PILLAR_CHAIR_EXPORT,
                icon: FileDown,
                // No permission or role gate: every account may export. What differs
                // per role is the scope, which the server derives from the account.
            },
            // Access is a per-account grant an admin hands out, not a
            // permission bit, so filterNavItems cannot gate it — the entry is
            // simply absent until the grant resolves.
            ...(hasOperationalDashboard
                ? [
                      {
                          label: 'Operational Dashboard',
                          path: ROUTES.PILLAR_CHAIR_OPERATIONAL_DASHBOARD,
                          icon: MonitorCog,
                      },
                  ]
                : []),
            {
                label: 'User Manual',
                path: ROUTES.PILLAR_CHAIR_HELP,
                icon: BookOpen,
                requiredPermissions: [PERMISSIONS.VIEW_PILLAR],
            },
        ],
        [hasOperationalDashboard],
    );

    const visibleTabs = useMemo(() => filterNavItems(user, tabs), [user, tabs]);

    return (
        <DashboardHeaderSlotProvider>
            <ChatProvider>
                <AiAssistantProvider>
            <div className="flex h-screen h-dvh flex-col gap-3 overflow-hidden bg-background p-3">
                <ExecutiveAppHeader
                    title="Pillar Dashboard"
                    subtitle={pillarNameSubtitle}
                    center={<DashboardHeaderSlot />}
                    showCountdown
                    onOpenSidebar={() => setSidebarOpen(true)}
                />

                <div className="relative flex min-h-0 flex-1 overflow-hidden">
                    {isMobile && sidebarOpen ? (
                        <div
                            className="fixed inset-0 z-30 bg-black/40 md:hidden"
                            onClick={() => setSidebarOpen(false)}
                            aria-hidden
                        />
                    ) : null}
                    <aside
                        className={cn(
                            APP_SIDEBAR_ASIDE_CLASS,
                            isMobile || sidebarOpen ? 'w-64' : 'w-14',
                            isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0',
                        )}
                    >
                        <div className={APP_SIDEBAR_HEADER_ROW_CLASS}>
                            {sidebarOpen ? (
                                <div className="flex min-w-[16rem] w-full justify-end px-2 py-2">
                                    <button
                                        type="button"
                                        onClick={() => setSidebarOpen(false)}
                                        className={APP_SIDEBAR_TOGGLE_BUTTON_CLASS}
                                        aria-label="Collapse sidebar to icons only"
                                        title="Show icons only"
                                    >
                                        <TripleChevronCloseIcon />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex w-full justify-center py-2">
                                    <button
                                        type="button"
                                        onClick={() => setSidebarOpen(true)}
                                        className={APP_SIDEBAR_TOGGLE_BUTTON_CLASS}
                                        aria-label="Expand sidebar"
                                        title="Expand sidebar"
                                    >
                                        <TripleChevronOpenIcon />
                                    </button>
                                </div>
                            )}
                        </div>
                        <nav className={appSidebarNavClassName(sidebarOpen)}>
                            {sidebarOpen ? (
                                <p className={APP_SIDEBAR_SECTION_LABEL_CLASS}>
                                    Navigation
                                </p>
                            ) : null}
                            {visibleTabs.map(({ requiredPermissions: _rp, requiredRole: _rr, ...tab }) => (
                                <NavLink
                                    key={tab.path}
                                    to={tab.path}
                                    end={tab.end}
                                    title={tab.label}
                                    onClick={() => isMobile && setSidebarOpen(false)}
                                    className={({ isActive }) =>
                                        appSidebarNavLinkClassName({ isActive, expanded: sidebarOpen })
                                    }
                                >
                                    <tab.icon className="h-5 w-5 shrink-0" aria-hidden />
                                    <span className={cn('leading-tight', !sidebarOpen && 'sr-only')}>
                                        {tab.label}
                                    </span>
                                </NavLink>
                            ))}
                            <AiAssistantNavItem
                                expanded={sidebarOpen}
                                onActivate={() => isMobile && setSidebarOpen(false)}
                            />
                        </nav>
                    </aside>

                    <main
                        className={cn(
                            'min-h-0 flex-1 p-0.5 md:p-3',
                            isPillarChairDashboardHome && !isMobile
                                ? 'flex flex-col overflow-hidden'
                                : 'overflow-y-auto overflow-x-hidden',
                        )}
                    >
                        <Outlet context={pillarOutletCtx} />
                    </main>
                </div>
            </div>
                </AiAssistantProvider>
            </ChatProvider>
        </DashboardHeaderSlotProvider>
    );
}

export default PillarChairLayout;
